import { ColumnsType, defaultTheme } from '@agro-club/agroclub-shared'
import { TFunction } from 'i18next'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { EditRoadRatesCell } from './components/EditRoadRatesCell'
import { RegionCell } from './components/RegionCell'
import { countryFlags } from './countryFlags'
import { RangeType, RatesProductTypes, RateType } from './types'
import { RateCell } from './components/RateCell'

const getColumns = (
  ranges: RangeType[],
  t: TFunction,
  onSuccess: () => void,
  currentProductType?: RatesProductTypes,
) => {
  const rangeColumns = ranges.map((range) => ({
    key: range.upper.toString(),
    title: range.upper,
    width: 60,
    align: 'right',
    dataIndex: 'logistics_rates',
    render: (logistics_rates: RateType[]) => <RateCell logistics_rates={logistics_rates} range={range} />,
    onCell: (record) => {
      const rates: RateType[] = record.logistics_rates
      const rate = rates.find(
        (rate) => rate.distance_range.lower === range.lower && rate.distance_range.upper === range.upper,
      )
      let cellColor: string | undefined
      if (rate?.history_info) {
        if ((rate.rate || 0) > (rate.history_info.rate || 0)) {
          cellColor = defaultTheme.color.accentDestruct16a
        } else if ((rate.rate || 0) < (rate.history_info.rate || 0)) {
          cellColor = defaultTheme.color.accentApprove16a
        }
      }
      return { style: { backgroundColor: cellColor } }
    },
  }))

  const results = [
    {
      title: t('region'),
      width: 144,
      align: 'left',
      dataIndex: 'name',
      render: (name, data) => (
        <RegionCell regionName={name} logistician={data.responsible_logisticians[0]} updated={data.updated} />
      ),
    },
    {
      title: '',
      width: 56,
      align: 'center',
      dataIndex: 'country_code',
      render: (country_code) => {
        const Icon = countryFlags[country_code]
        if (!Icon) return
        return <Icon style={{ height: 20 }} />
      },
    },
    ...rangeColumns,
    {
      title: '',
      width: 50,
      dataIndex: 'id',
      align: 'center',
      fixed: 'right',
      render: (id, data) => (
        <EditRoadRatesCell
          regionIds={[id]}
          onSuccess={onSuccess}
          ratesData={data.logistics_rates}
          responsibleLogisticians={data.responsible_logisticians}
          currentProductType={currentProductType}
        />
      ),
    },
  ] as ColumnsType<any>

  return results
}

export const useColumns = (
  ranges: RangeType[],
  onSuccess: () => void,
  currentProductType?: RatesProductTypes,
): ColumnsType<any> => {
  const { t } = useTranslation('rates')

  return useMemo(() => getColumns(ranges, t, onSuccess, currentProductType), [ranges, t, currentProductType, onSuccess])
}
